import { ThemeColorName } from '@examdojo/styles';
import { ensureArray } from '@examdojo/util/ensure-array';
import { isNullish } from '@examdojo/util/nullish';
import { OrArray } from '@ngneat/elf';
import type { Board, GeometryElement, Text } from 'jsxgraph';
import { InferActionTarget, InferActionType } from '../../model';
import type {
  ActionDefinition,
  ActionResult,
  EasingFunction,
  TransitionProfile,
} from '../../model/widget-action.model';
import { type BaseWidget, WidgetEvent, type WidgetEventCallback } from '../../model/widget-base.model';
import type { WidgetConfig, WidgetState } from '../../model/widget-config.model';
import type { EventMap, WidgetEventSelectors } from '../../model/widget-event.model';
import type { JsxgraphThemeService } from './jsxgraph-theme.service';
import type {
  AxesFeatureConfig,
  InteractionFeatureConfig,
  JsxgraphActionType,
  JsxgraphBoardOptions,
  JsxgraphStatic,
  LabelConfig,
  LabelVariant,
  LineStyleOverrides,
  SlidersFeatureConfig,
  SliderStyleOverrides,
} from './jsxgraph.model';
import {
  DEFAULT_AXES_CONFIG,
  DEFAULT_BOARD_OPTIONS,
  DEFAULT_DISPLAY_CONFIG,
  DEFAULT_INTERACTION_CONFIG,
  DEFAULT_SLIDERS_CONFIG,
  DEFAULT_TRANSITIONS,
  normalizeConfig,
} from './jsxgraph.model';

/**
 * Extended Text interface with updateText method.
 * The method exists at runtime but is missing from JSXGraph's type definitions.
 */
interface TextElement extends Text {
  updateText(): void;
}

/**
 * Abstract base class for JSXGraph-based widgets.
 * Handles common functionality like board management, events, animations, and theming.
 *
 * This class builds JSXGraph API options internally by combining:
 * - Feature configs (what to show)
 * - Theme values (how it looks)
 */
export abstract class JsxgraphBaseWidget<Config extends WidgetConfig = WidgetConfig> implements BaseWidget<Config> {
  constructor(
    protected container: HTMLElement,
    jsxInstance: JsxgraphStatic,
    protected themeService: JsxgraphThemeService,
  ) {
    this.jsxInstance = jsxInstance;
  }

  abstract id: string;
  abstract type: Config['type'];

  protected board!: Board;
  protected jsxInstance: JsxgraphStatic;
  protected objects: Map<string, GeometryElement> = new Map();
  protected eventHandlers: Map<string, Set<WidgetEventCallback>> = new Map();
  protected isDestroyed = false;
  private isAnimating = false;

  /** Instance-level transition overrides from behavior config */
  protected instanceTransitions: Record<string, TransitionProfile> = {};

  /** Whether grid is enabled - used by createAxes to determine tick height */
  private gridEnabled = false;

  // ==============================================================================
  // ACTION SYSTEM
  // ==============================================================================

  /**
   * Execute an action with optional transition.
   * Returns result with success flag and optional error message.
   */
  async executeAction(action: ActionDefinition<InferActionType<Config>>): Promise<ActionResult> {
    if (this.isDestroyed) {
      return { success: false, error: 'Widget has been destroyed' };
    }

    const transition = this.resolveTransition(action.transition);

    // Handle delay if specified
    if (transition?.delay && transition.delay > 0) {
      await this.delay(transition.delay);
    }

    const result = await this.performAction(action.action, action.payload, transition);
    if (result.success) {
      this.emit(WidgetEvent.AnimationComplete, { action: action.action, payload: action.payload });
    }
    return result;
  }

  /**
   * Initialize the widget with config.
   * Subclasses must implement this to create their specific visualizations.
   */
  abstract init(config: Config): void;

  /**
   * Get the current config for merging during updates.
   * Subclasses must implement this to return their stored config.
   */
  protected abstract getCurrentConfig(): Config | undefined;

  /**
   * Update widget config dynamically.
   * Default implementation destroys and reinitializes.
   * Override for more efficient updates.
   */
  update(config: Partial<Config>): void {
    const currentConfig = this.getCurrentConfig();
    if (!currentConfig) {
      console.error('Cannot update widget: no config available');
      return;
    }

    const newConfig = { ...currentConfig, ...config };
    this.destroy();
    this.init(newConfig);
    this.emit(WidgetEvent.StateChange, this.getState());
  }

  /**
   * Clean up the widget and free resources.
   */
  destroy(): void {
    if (this.isDestroyed) {
      return;
    }

    if (this.board && this.jsxInstance) {
      try {
        this.jsxInstance.JSXGraph.freeBoard(this.board);
      } catch (error) {
        console.error('Error freeing JSXGraph board:', error);
      }
    }

    this.objects.clear();
    this.eventHandlers.clear();
    this.isDestroyed = true;

    (this as unknown as { board: Board | null }).board = null;
  }

  /**
   * Get current widget state for serialization.
   */
  abstract getState(): WidgetState;

  /**
   * Restore widget from a saved state.
   */
  setState(state: WidgetState): void {
    this.destroy();
    this.init(state as unknown as Config);
  }

  /**
   * Subscribe to widget events.
   */
  on(event: string, callback: WidgetEventCallback): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(callback);
  }

  /**
   * Unsubscribe from widget events.
   */
  off(event: string, callback: WidgetEventCallback): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(callback);
    }
  }

  /**
   * Hook for subclasses to override for custom actions.
   * Returns result with success flag and optional error message.
   */
  protected async performAction(
    action: InferActionType<Config>,
    payload?: Record<string, unknown>,
    transition?: TransitionProfile,
  ): Promise<ActionResult> {
    switch (action as JsxgraphActionType) {
      case 'FOCUS_REGION':
        return this.actionFocusRegion(payload, transition);
      case 'TRANSFORM_VIEW':
        return this.actionTransformView(payload, transition);
      case 'PATCH_STYLE':
        return this.actionPatchStyle(payload, transition);
      case 'HIGHLIGHT':
        return this.actionHighlight(payload, transition);
      case 'RESET_VIEW':
        return this.actionResetView(transition);
      default:
        return { success: false, error: `Unknown action '${action}'` };
    }
  }

  /**
   * Resolve a transition reference to a full TransitionProfile.
   * Looks up string references in instance transitions, then global defaults.
   */
  protected resolveTransition(transition?: string | Partial<TransitionProfile>): TransitionProfile | undefined {
    if (!transition) {
      return DEFAULT_TRANSITIONS['default'];
    }

    if (typeof transition === 'string') {
      return this.instanceTransitions[transition] || DEFAULT_TRANSITIONS[transition] || DEFAULT_TRANSITIONS['default'];
    }

    // Merge partial with default
    return { ...DEFAULT_TRANSITIONS['default'], ...transition };
  }

  /**
   * Get the easing function for interpolation.
   */
  protected getEasingFunction(easing: EasingFunction): (t: number) => number {
    switch (easing) {
      case 'linear':
        return (t) => t;
      case 'easeIn':
        return (t) => t * t * t;
      case 'easeOut':
        return (t) => 1 - Math.pow(1 - t, 3);
      case 'easeInOut':
        return (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      case 'elastic':
        return (t) => {
          const c4 = (2 * Math.PI) / 3;
          return t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
        };
      case 'bounce':
        return (t) => {
          const n1 = 7.5625;
          const d1 = 2.75;
          if (t < 1 / d1) {
            return n1 * t * t;
          }
          if (t < 2 / d1) {
            return n1 * (t -= 1.5 / d1) * t + 0.75;
          }
          if (t < 2.5 / d1) {
            return n1 * (t -= 2.25 / d1) * t + 0.9375;
          }
          return n1 * (t -= 2.625 / d1) * t + 0.984375;
        };
      default:
        return (t) => t;
    }
  }

  // ==============================================================================
  // BUILT-IN ACTIONS
  // ==============================================================================

  private async actionFocusRegion(
    payload?: Record<string, unknown>,
    transition?: TransitionProfile,
  ): Promise<ActionResult> {
    const region = payload?.['region'] as string | number | undefined;
    if (isNullish(region)) {
      return { success: false, error: 'Missing region in payload' };
    }
    if (!this.board) {
      return { success: false, error: 'Board not initialized' };
    }

    const bbox = this.getRegionBoundingBox(region);
    if (!bbox) {
      return { success: false, error: `Unknown region '${region}'` };
    }

    await this.animateBoundingBox(bbox, transition);
    return { success: true };
  }

  private async actionTransformView(
    payload?: Record<string, unknown>,
    transition?: TransitionProfile,
  ): Promise<ActionResult> {
    const mode = payload?.['mode'] as string;
    const factor = (payload?.['factor'] as number) ?? 1.1;

    if (!this.board) {
      return { success: false, error: 'Board not initialized' };
    }

    const currentBbox = this.board.getBoundingBox();
    let newBbox: [number, number, number, number];

    switch (mode) {
      case 'zoom_in':
        newBbox = this.scaleBoundingBox(currentBbox, 1 / factor);
        break;
      case 'zoom_out':
        newBbox = this.scaleBoundingBox(currentBbox, factor);
        break;
      default:
        return { success: false, error: `Unknown transform mode '${mode}'` };
    }

    await this.animateBoundingBox(newBbox, transition);
    return { success: true };
  }

  private async actionPatchStyle(
    payload?: Record<string, unknown>,
    _transition?: TransitionProfile,
  ): Promise<ActionResult> {
    const target = payload?.['target'] as string;
    if (!target) {
      return { success: false, error: 'Missing target in payload' };
    }

    const obj = this.objects.get(target);
    if (!obj) {
      return { success: false, error: `Object '${target}' not found` };
    }

    const styleProps = { ...payload };
    delete styleProps['target'];

    Object.entries(styleProps).forEach(([key, value]) => {
      (obj as unknown as { setAttribute: (attrs: Record<string, unknown>) => void }).setAttribute({ [key]: value });
    });
    return { success: true };
  }

  private async actionHighlight(
    payload?: Record<string, unknown>,
    transition?: TransitionProfile,
  ): Promise<ActionResult> {
    const objects = payload?.['objects'] as string[] | undefined;
    if (!objects?.length) {
      return { success: false, error: 'Missing objects array in payload' };
    }

    const duration = transition?.duration ?? 300;
    const promises: Array<Promise<void>> = [];

    for (const objId of objects) {
      const obj = this.objects.get(objId);
      if (obj) {
        promises.push(this.highlightObject(obj, duration));
      }
    }

    if (promises.length === 0) {
      return { success: false, error: 'No matching objects found' };
    }

    await Promise.all(promises);
    return { success: true };
  }

  private async actionResetView(transition?: TransitionProfile): Promise<ActionResult> {
    if (!this.board) {
      return { success: false, error: 'Board not initialized' };
    }
    const defaultBbox = DEFAULT_DISPLAY_CONFIG.boundingbox!;
    await this.animateBoundingBox(defaultBbox, transition);
    return { success: true };
  }

  private scaleBoundingBox(bbox: [number, number, number, number], factor: number): [number, number, number, number] {
    const centerX = (bbox[0] + bbox[2]) / 2;
    const centerY = (bbox[1] + bbox[3]) / 2;
    const halfWidth = ((bbox[2] - bbox[0]) / 2) * factor;
    const halfHeight = ((bbox[1] - bbox[3]) / 2) * factor;

    return [centerX - halfWidth, centerY + halfHeight, centerX + halfWidth, centerY - halfHeight];
  }

  // ==============================================================================
  // EVENT WIRING
  // ==============================================================================

  /**
   * Wire event selectors to objects during initialization.
   * Call this in subclass init() after creating objects.
   */
  protected wireEvents(selectors?: WidgetEventSelectors<InferActionTarget<Config>, InferActionType<Config>>): void {
    if (!selectors || !this.board) {
      return;
    }

    Object.entries(selectors).forEach(([key, eventMap]) => {
      if (!eventMap) {
        return;
      }

      if (key === 'canvas') {
        this.wireCanvasEvents(eventMap);
      } else {
        const obj = this.objects.get(key);
        if (obj) {
          this.wireObjectEvents(obj, eventMap);
        }
      }
    });
  }

  private wireCanvasEvents(eventMap: EventMap<InferActionType<Config>>): void {
    // Wire board-level events
    if (eventMap.onClick) {
      this.board.on('down', () => this.executeEventActions(eventMap.onClick!));
    }
    if (eventMap.onScroll) {
      this.board.on('mousewheel', () => this.executeEventActions(eventMap.onScroll!));
    }
  }

  private wireObjectEvents(obj: GeometryElement, eventMap: EventMap<InferActionType<Config>>): void {
    if (eventMap.onHover) {
      obj.on('over', () => this.executeEventActions(eventMap.onHover!));
    }
    if (eventMap.onHoverOut) {
      obj.on('out', () => this.executeEventActions(eventMap.onHoverOut!));
    }
    if (eventMap.onClick) {
      obj.on('down', () => this.executeEventActions(eventMap.onClick!));
    }
    if (eventMap.onDrag) {
      obj.on('drag', () => this.executeEventActions(eventMap.onDrag!));
    }
  }

  private async executeEventActions(actions: OrArray<ActionDefinition<InferActionType<Config>>>): Promise<void> {
    const actionArray = ensureArray(actions);
    for (const action of actionArray) {
      await this.executeAction(action);
    }
  }

  // ==============================================================================
  // UTILITY
  // ==============================================================================

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Emit an event to all subscribers.
   */
  protected emit(event: string, data: unknown): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event handler for '${event}':`, error);
        }
      });
    }
  }

  /**
   * Animate board bounding box change using TransitionProfile.
   */
  protected animateBoundingBox(bbox: [number, number, number, number], transition?: TransitionProfile): Promise<void> {
    return new Promise((resolve) => {
      if (!this.board || this.isAnimating) {
        resolve();
        return;
      }

      const profile = transition ?? DEFAULT_TRANSITIONS['default'];

      // Instant transition (no animation)
      if (profile.type === 'none' || profile.duration === 0) {
        this.board.setBoundingBox(bbox, false);
        resolve();
        return;
      }

      // Check if already at target (prevent unnecessary animation)
      const currentBbox = this.board.getBoundingBox();
      const threshold = 0.01;
      const isSame =
        Math.abs(currentBbox[0] - bbox[0]) < threshold &&
        Math.abs(currentBbox[1] - bbox[1]) < threshold &&
        Math.abs(currentBbox[2] - bbox[2]) < threshold &&
        Math.abs(currentBbox[3] - bbox[3]) < threshold;

      if (isSame) {
        resolve();
        return;
      }

      this.isAnimating = true;
      const startBbox: [number, number, number, number] = [
        currentBbox[0],
        currentBbox[1],
        currentBbox[2],
        currentBbox[3],
      ];

      const steps = Math.max(1, Math.round(profile.duration / 16)); // ~60fps
      const stepDuration = profile.duration / steps;
      const easingFn = this.getEasingFunction(profile.easing);
      let step = 0;

      const interpolate = (start: number, end: number, progress: number): number => {
        const t = easingFn(progress);
        return start + (end - start) * t;
      };

      const animateStep = () => {
        if (!this.board || this.isDestroyed) {
          this.isAnimating = false;
          resolve();
          return;
        }

        step++;
        const progress = step / steps;

        const newBbox: [number, number, number, number] = [
          interpolate(startBbox[0], bbox[0], progress),
          interpolate(startBbox[1], bbox[1], progress),
          interpolate(startBbox[2], bbox[2], progress),
          interpolate(startBbox[3], bbox[3], progress),
        ];

        this.board.setBoundingBox(newBbox, false);

        if (step < steps) {
          setTimeout(animateStep, stepDuration);
        } else {
          this.isAnimating = false;
          resolve();
        }
      };

      animateStep();
    });
  }

  /**
   * Highlight a single object with fade animation.
   */
  protected highlightObject(obj: GeometryElement, duration = 300): Promise<void> {
    return new Promise((resolve) => {
      (obj as unknown as { highlight: () => void }).highlight();

      setTimeout(() => {
        (obj as unknown as { noHighlight: () => void }).noHighlight();
        resolve();
      }, duration);
    });
  }

  /**
   * Get bounding box for a named region.
   * Subclasses override to define custom regions.
   */
  protected getRegionBoundingBox(_region: string | number): [number, number, number, number] | null {
    // Default: unknown regions return null - subclasses should override for specific regions
    return null;
  }

  /**
   * Force text labels to re-evaluate their content functions.
   * Call this after board.update() when parameters affecting label text change.
   *
   * Labels are identified by keys ending with '-label' in the objects map.
   */
  protected updateLabels(): void {
    [...this.objects.entries()]
      .filter((entry): entry is [string, TextElement] => entry[0].endsWith('-label'))
      .forEach(([, obj]) => obj.updateText());
  }

  // ==============================================================================
  // BOARD INITIALIZATION
  // ==============================================================================

  /**
   * Initialize JSXGraph board with theme defaults.
   * @param options - JSXGraph board options
   * @param interaction - Interaction feature config (zoom, pan, drag)
   * @param backgroundColor - Optional background color override
   */
  protected initBoard(
    options: JsxgraphBoardOptions = {},
    interaction?: InteractionFeatureConfig | boolean,
    backgroundColor?: string,
  ): void {
    // Free any existing board before creating a new one to prevent memory leaks
    if (this.board && this.jsxInstance) {
      try {
        this.jsxInstance.JSXGraph.freeBoard(this.board);
      } catch (error) {
        console.error('Error freeing existing JSXGraph board:', error);
      }
    }

    this.isDestroyed = false;

    const containerId = `jsx-${this.id}-${Date.now()}`;
    this.container.id = containerId;

    const theme = this.themeService.getTheme();

    // Apply frame styling to container
    this.container.style.borderRadius = theme.spacing.borderRadius;
    this.container.style.overflow = 'hidden';
    this.container.style.border = `1px solid ${theme.colors.border}`;
    this.container.style.backgroundColor = backgroundColor || theme.colors.background;

    // Apply interaction config
    const interactionConfig = normalizeConfig(interaction, DEFAULT_INTERACTION_CONFIG);
    const interactionOptions: JsxgraphBoardOptions = interactionConfig.enabled
      ? {
          pan: { enabled: interactionConfig.pannable ?? true },
          zoom: interactionConfig.zoomable ?? true,
        }
      : {
          pan: { enabled: false },
          zoom: false,
        };

    const boardOptions: JsxgraphBoardOptions = {
      ...DEFAULT_BOARD_OPTIONS,
      ...interactionOptions,
      ...options,
    };

    this.board = this.jsxInstance.JSXGraph.initBoard(containerId, boardOptions);
    this.emit(WidgetEvent.Ready, { boardId: containerId });
  }

  // ==============================================================================
  // AXES (builds JSXGraph options from theme + feature config)
  // ==============================================================================

  /**
   * Create axes with theme styling.
   * Builds JSXGraph API options internally from theme values.
   *
   * When grid is enabled, major ticks extend across the board (majorHeight: -1).
   * When grid is disabled, ticks are short marks on the axis.
   */
  protected createAxes(feature?: AxesFeatureConfig | boolean): void {
    if (!this.board) {
      return;
    }

    const config = normalizeConfig(feature, DEFAULT_AXES_CONFIG);
    if (!config.enabled) {
      return;
    }

    const theme = this.themeService.getTheme();

    // Build JSXGraph axis options from theme
    const axisOptions = {
      strokeColor: theme.colors.axis,
      strokeWidth: 1,
      highlight: false,
    };

    // When grid is enabled: majorHeight: -1 extends ticks infinitely (creates grid lines)
    // When grid is disabled: majorHeight: -15 creates short tick marks (negative = pixels)
    const ticksOptions =
      config.ticks !== false
        ? {
            visible: true,
            insertTicks: true,
            drawLabels: config.labels !== false,
            strokeColor: theme.colors.axis,
            strokeWidth: 2,
            majorHeight: this.gridEnabled ? -1 : 5, // -1 = infinite (grid), -15 = 15 pixels (short ticks)
            minorHeight: 0,
            minorTicks: 0,
            label: {
              fontSize: theme.typography.fontSize.small,
              cssStyle: `font-family: ${theme.typography.fontFamily}; color: ${theme.colors.textSecondary};`,
            },
          }
        : false;

    if (config.xAxis !== false) {
      const xAxis = this.board.create(
        'axis',
        [
          [0, 0],
          [1, 0],
        ],
        { ...axisOptions, name: 'x', withLabel: false, ticks: ticksOptions },
      ) as GeometryElement;
      this.objects.set('x-axis', xAxis);
    }

    if (config.yAxis !== false) {
      const yAxis = this.board.create(
        'axis',
        [
          [0, 0],
          [0, 1],
        ],
        { ...axisOptions, name: 'y', withLabel: false, ticks: ticksOptions },
      ) as GeometryElement;
      this.objects.set('y-axis', yAxis);
    }
  }

  // ==============================================================================
  // GRID (controls axis tick extension)
  // ==============================================================================

  /**
   * Enable grid display.
   * Grid is implemented by extending axis ticks across the board.
   * Must be called BEFORE createAxes() to take effect.
   * @param show - Whether to show the grid (default: false)
   */
  protected createGrid(show?: boolean): void {
    // Grid is implemented via axis ticks with majorHeight: -1
    // This flag is read by createAxes() to determine tick configuration
    this.gridEnabled = !!show;
  }

  // ==============================================================================
  // SLIDERS (builds JSXGraph options from theme + feature config)
  // ==============================================================================

  /**
   * Create a slider with theme styling.
   * Builds JSXGraph API options internally from theme values.
   */
  protected createSlider(
    name: string,
    range: [number, number, number],
    position: [number, number],
    overrides?: SliderStyleOverrides,
  ): GeometryElement {
    if (!this.board) {
      throw new Error('Board not initialized');
    }

    const theme = this.themeService.getTheme();
    const baseColor = theme.colors.feature[0];

    // Build JSXGraph slider options from theme
    const sliderOptions = {
      name,
      strokeColor: overrides?.strokeColor ?? baseColor,
      strokeWidth: overrides?.strokeWidth ?? 2,
      fillColor: overrides?.fillColor ?? theme.colors.background,
      highlightStrokeColor: overrides?.highlightStrokeColor ?? theme.colors.feature[1],
      snapWidth: overrides?.snapWidth,
      baseline: {
        strokeColor: theme.colors.grid,
        strokeWidth: 1,
      },
      highline: {
        strokeColor: baseColor,
        strokeWidth: 2,
      },
      label: {
        fontSize: theme.typography.fontSize.base,
        fontWeight: theme.typography.fontWeight.medium,
        color: theme.colors.textSecondary,
      },
    };

    const [x, y] = position;
    const sliderLength = theme.spacing.sliderLength;

    const slider = this.board.create('slider', [[x, y], [x + sliderLength, y], range], sliderOptions);

    this.objects.set(`slider-${name}`, slider as GeometryElement);
    return slider as GeometryElement;
  }

  /**
   * Create multiple sliders based on slider feature configuration.
   * Supports per-slider styling via individualStyles in config.
   */
  protected createSliders(
    parameters: Array<{ name: string; range: [number, number, number] }>,
    feature?: SlidersFeatureConfig | boolean,
  ): Map<string, GeometryElement> {
    const sliders = new Map<string, GeometryElement>();

    const config = normalizeConfig(feature, DEFAULT_SLIDERS_CONFIG);
    if (!config.enabled) {
      return sliders;
    }

    const bbox = this.board.getBoundingBox();
    const xStart = bbox[0] + 1;
    const yStart = bbox[3] + 4;

    parameters.forEach((param, index) => {
      const y = yStart - index * 1.2;
      // Merge global snapWidth with individual styles for this parameter
      const individualStyle = config.individualStyles?.[param.name];
      const slider = this.createSlider(param.name, param.range, [xStart, y], {
        snapWidth: config.snapWidth,
        ...individualStyle,
      });
      sliders.set(param.name, slider);
    });

    return sliders;
  }

  /**
   * Helper to get slider value safely with type casting.
   */
  protected getSliderValue(slider: GeometryElement): number {
    return (slider as never as { Value: () => number }).Value();
  }

  /**
   * Helper to set slider value safely with type casting.
   */
  protected setSliderValue(slider: GeometryElement, value: number): void {
    (slider as never as { setValue: (v: number) => void }).setValue(value);
  }

  // ==============================================================================
  // LINES (builds JSXGraph options from theme + overrides)
  // ==============================================================================

  /**
   * Get line style options from theme with optional overrides.
   */
  protected getLineStyle(overrides?: LineStyleOverrides): Record<string, unknown> {
    const strokeColor =
      overrides?.strokeColor ||
      (overrides?.colorName ? this.themeService.getColor(overrides.colorName) : this.themeService.getColor('sky'));

    return {
      strokeColor,
      strokeWidth: overrides?.strokeWidth ?? 2,
      dash: overrides?.dash ?? 0,
    };
  }

  // ==============================================================================
  // LABELS (builds JSXGraph options from theme + config)
  // ==============================================================================

  /**
   * Convert pixel offset to coordinate offset based on current board zoom level.
   * This ensures labels maintain consistent visual spacing regardless of zoom.
   */
  protected pixelsToCoords(pixels: number, axis: 'x' | 'y'): number {
    if (!this.board || !this.container) {
      return 0;
    }

    const bbox = this.board.getBoundingBox();
    const containerRect = this.container.getBoundingClientRect();

    if (axis === 'x') {
      const coordWidth = bbox[2] - bbox[0];
      return pixels * (coordWidth / containerRect.width);
    } else {
      const coordHeight = bbox[1] - bbox[3];
      return pixels * (coordHeight / containerRect.height);
    }
  }

  /**
   * Create a label with variant-aware styling.
   * Builds JSXGraph API options internally from theme values.
   * @param labelText - Static string or function returning string for dynamic labels
   */
  protected createLabel(
    point: GeometryElement,
    labelText: string | (() => string),
    config: LabelConfig,
    offset?: [number, number],
  ): GeometryElement {
    const variant: LabelVariant = config.variant ?? 'secondary';
    const color: ThemeColorName = config.color ?? 'neutral';

    const theme = this.themeService.getTheme();
    const labelStyle = this.themeService.getLabelStyle(variant, color);
    const hasBackground = variant !== 'tertiary';
    const labelOffset = offset ?? theme.spacing.labelOffset;

    // Support both static strings and dynamic functions for label text
    const getText = typeof labelText === 'function' ? labelText : () => labelText;

    const typedPoint = point as unknown as { X: () => number; Y: () => number };
    const label = this.board.create(
      'text',
      [
        () => typedPoint.X() + this.pixelsToCoords(labelOffset[0], 'x'),
        () => typedPoint.Y() + this.pixelsToCoords(labelOffset[1], 'y'),
        () => `<span style="
          ${hasBackground ? `background-color: ${labelStyle.backgroundColor};` : ''}
          color: ${labelStyle.textColor};
          ${hasBackground ? `padding: ${theme.spacing.labelPadding};` : ''}
          ${hasBackground ? 'border-radius: 6px;' : ''}
          font-size: ${theme.typography.fontSize.label}px;
          font-weight: ${theme.typography.fontWeight.bold};
          font-family: ${theme.typography.fontFamily};
          white-space: nowrap;
          ${hasBackground ? 'display: inline-block;' : ''}
        ">${getText()}</span>`,
      ],
      {
        anchorX: 'left',
        anchorY: 'middle',
        parse: false,
        useMathjax: false,
        useMathJax: false,
        cssClass: `jsx-${variant}-label-html`,
      },
    );

    return label as GeometryElement;
  }
}
